import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Input,
  Skeleton,
} from "@resume-studio/ui";
import { AlertTriangle, ArrowLeft, ExternalLink, Trash2 } from "lucide-react";
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";

import { toast } from "@/admin/hooks/use-toast";
import { useAdminSession, useAdminUser, useDeleteAdminUser } from "@/admin/services";

import { Card } from "../../../components/ui/card";
import { PageHeader } from "../../_components/page-header";

export const AdminUserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminUser(id);
  const { user: currentAdmin } = useAdminSession();
  const deleteMutation = useDeleteAdminUser(id);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [confirmationEmail, setConfirmationEmail] = React.useState("");

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Loading…" trail={["Admin", "Users"]} />
        <Skeleton className="h-32 w-full" />
      </>
    );
  }

  const normalizedConfirmation = confirmationEmail.trim().toLowerCase();
  const canConfirmDelete = normalizedConfirmation === data.email.toLowerCase();
  const deleteBlockedReason =
    data.role === "admin"
      ? "Admin accounts cannot be deleted from user management."
      : currentAdmin?.id === data.id
        ? "You cannot delete your own admin session."
        : null;

  const handleDeleteUser = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canConfirmDelete || deleteBlockedReason) return;

    deleteMutation.mutate(
      { confirmationEmail: confirmationEmail.trim() },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          toast({
            variant: "success",
            title: "User deleted",
            description: "The account and associated data were removed.",
          });
          navigate("/users");
        },
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>{data.email} · Admin</title>
      </Helmet>

      <PageHeader
        title={data.name || data.email}
        trail={["Admin", "Users"]}
        description={data.email}
        actions={
          <>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/users" className="gap-1">
                <ArrowLeft className="size-3.5" />
                Back
              </Link>
            </Button>
            {data.deepLinks.stripeCustomer && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={data.deepLinks.stripeCustomer}
                  target="_blank"
                  rel="noreferrer"
                  className="gap-1"
                >
                  Stripe
                  <ExternalLink className="size-3.5" />
                </a>
              </Button>
            )}
            {data.deepLinks.posthogPerson && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={data.deepLinks.posthogPerson}
                  target="_blank"
                  rel="noreferrer"
                  className="gap-1"
                >
                  PostHog
                  <ExternalLink className="size-3.5" />
                </a>
              </Button>
            )}
          </>
        }
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Account">
          <dl className="space-y-2.5">
            <Row label="ID" value={data.id} mono />
            <Row label="Username" value={data.username} />
            <Row label="Provider" value={data.provider} />
            <Row label="Email verified" value={data.emailVerified ? "Yes" : "No"} />
            <Row label="2FA" value={data.twoFactorEnabled ? "Enabled" : "Disabled"} />
            <Row label="Locale" value={data.locale} />
            <Row label="Joined" value={new Date(data.createdAt).toLocaleString()} />
            {data.role === "admin" && <Row label="Role" value="Admin" />}
          </dl>
        </Card>

        <Card title="Subscription">
          {data.subscription ? (
            <dl className="space-y-2.5">
              <Row label="Plan" value={data.subscription.plan} />
              <Row label="Status" value={data.subscription.status} />
              <Row
                label="Current period"
                value={
                  data.subscription.currentPeriodStart && data.subscription.currentPeriodEnd
                    ? `${new Date(data.subscription.currentPeriodStart).toLocaleDateString()} → ${new Date(data.subscription.currentPeriodEnd).toLocaleDateString()}`
                    : "—"
                }
              />
              <Row
                label="Cancel at period end"
                value={data.subscription.cancelAtPeriodEnd ? "Yes" : "No"}
              />
              {data.subscription.stripeSubscriptionId && (
                <Row label="Stripe sub" value={data.subscription.stripeSubscriptionId} mono />
              )}
            </dl>
          ) : (
            <p className="text-[13px] text-muted-foreground">No active subscription.</p>
          )}
        </Card>
      </section>

      <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Usage">
          <dl className="space-y-2.5">
            <Row label="Resumes" value={data.counts.resumes.toLocaleString()} />
            <Row label="Cover letters" value={data.counts.coverLetters.toLocaleString()} />
            <Row label="Job applications" value={data.counts.applications.toLocaleString()} />
            <Row label="Mock interviews" value={data.counts.interviews.toLocaleString()} />
          </dl>
        </Card>

        <Card title="Campaigns">
          {data.campaigns.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">No campaign activity.</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {data.campaigns.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-foreground">{c.campaignName}</p>
                    <p className="text-[12px] text-muted-foreground">
                      {c.kind === "conversion" ? "Converted" : "Claim"} ·{" "}
                      {new Date(c.grantedAt).toLocaleDateString()}
                      {c.discountPercent ? ` · ${c.discountPercent}% off` : ""}
                    </p>
                  </div>
                  {c.isActive && (
                    <span className="inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-success-foreground">
                      Active
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      <section className="mt-4">
        <Card
          title="Danger zone"
          description="Permanently remove this user account and the data tied to it."
          actions={
            <>
              <AlertTriangle className="mr-1 size-4 text-error" />
              <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={(open) => {
                  if (deleteMutation.isPending) return;
                  setDeleteDialogOpen(open);
                  if (!open) setConfirmationEmail("");
                }}
              >
                <AlertDialogTrigger asChild>
                  <Button variant="error" size="sm" disabled={Boolean(deleteBlockedReason)}>
                    <Trash2 className="mr-1.5 size-3.5" />
                    Delete user
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-md rounded-md">
                  <form onSubmit={handleDeleteUser} className="grid gap-4">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete user account</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the account, documents, AI conversations, usage
                        records, feedback, campaign records, billing customer/cache, trusted
                        devices, and stored files for this user.
                      </AlertDialogDescription>
                    </AlertDialogHeader>

                    <div className="grid gap-2">
                      <label
                        htmlFor="delete-user-confirmation"
                        className="text-[12px] font-medium text-foreground"
                      >
                        Type <span className="break-all font-semibold">{data.email}</span> to
                        confirm.
                      </label>
                      <Input
                        id="delete-user-confirmation"
                        value={confirmationEmail}
                        onChange={(event) => setConfirmationEmail(event.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        className="border-border bg-card text-[13px]"
                        disabled={deleteMutation.isPending}
                      />
                    </div>

                    <AlertDialogFooter className="gap-2 sm:space-x-0">
                      <AlertDialogCancel type="button" disabled={deleteMutation.isPending}>
                        Cancel
                      </AlertDialogCancel>
                      <Button
                        type="submit"
                        variant="error"
                        disabled={!canConfirmDelete || deleteMutation.isPending}
                      >
                        <Trash2 className="mr-1.5 size-3.5" />
                        {deleteMutation.isPending ? "Deleting…" : "Delete permanently"}
                      </Button>
                    </AlertDialogFooter>
                  </form>
                </AlertDialogContent>
              </AlertDialog>
            </>
          }
        >
          <p className="text-[13px] text-muted-foreground">
            {deleteBlockedReason ??
              "Deletion is immediate and audited. The confirmation email is checked again by the API before anything is removed."}
          </p>
        </Card>
      </section>
    </>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-[12px] text-muted-foreground">{label}</dt>
    <dd
      className={`text-[13px] tabular-nums text-foreground ${mono ? "mono max-w-[60%] truncate text-[12px] text-muted-foreground" : ""}`}
      title={mono ? value : undefined}
    >
      {value}
    </dd>
  </div>
);

export default AdminUserDetailPage;
